/* eslint-disable no-nested-ternary */
import styled from 'styled-components';
import {
  selectLoading, selectPeopleData,
  selectSearchText, selectSearchLoaded,
  selectSearchResultsPage, selectExtendedSearchLoaded,
  selectExtendedLoading,
  selectAffinitySelectedPersonId,
} from 'Selector/AffinitySearchSelector';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'Slice/AffinitySearchResultsSlice';
import { SearchResultSection, SearchResultsRequest } from 'Types/AffinitySearchTypes';
import { Executive, PersonCard } from 'Types/PersonTypes';
import { personCard } from 'Selector/PersonProfileSelector';
import { GetPersonProfileRequest } from 'Types/RequestBodies';
import { actions as personProfileActions } from 'Slice/PersonProfileSlice';

import InfiniteScroll from 'react-infinite-scroll-component';
import AffinitySearchResultItemSkeleton from 'Components/common/skeleton/AffinitySearchResultItemSkeleton';
import { actions as certificationActions } from 'Slice/CertificationSlice';
import { selectData } from 'Selector/CertificationSelector';
import { getLangCookie } from 'Utils/UtilityFunctions';
import AffinitySearchResultItem from './AffinitySearchResultItem';
import {
  primaryFont,
  wMedium,
  primaryBgColor,
  primaryColor,
  maxWidth,
  retina,
  boxShadow,
  scrollbar,
  maxTablet,
  maxMobile,
} from '../../../Utils/utilsStyle';

const ItemsWrapper = styled.div`
  top: 40px;
  z-index: -1;
  position: absolute;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: ${boxShadow};
  width: 93%;
  max-height: 270px;
  min-height: 55px !important;
  height: auto;
  overflow-y: auto;
  overflow-x: hidden;
   @media all and (max-width: ${maxWidth}) , ${retina} {
    width: 88%;
   }
   @media all and (max-width: ${maxTablet}) {
    width: 215px;
    margin-left: -25px;
   }
   @media all and (max-width: ${maxMobile}) {
    margin-left: 0px;
   }
    .m-left-50 {
      margin-left: 50px;
    }
   ${scrollbar} 
  .recent-search-text {
    color: #67696a;
    font-weight: ${wMedium};
    font-size: 18px;
    line-height: 34px;
    font-family: ${primaryFont};
    @media all and (max-width: ${maxWidth}) , ${retina} {
      font-size: 13px;
      line-height: 24px;
    }
  }
  .trending-topics-menu {
    background-color: ${primaryBgColor};
  }
  .padded-heading{
    padding: 25px 0px 30px 0px;
    border-radius: 0px 0px 25px 25px;
    @media all and (max-width: ${maxWidth}) , ${retina} {
      padding: 12px 0px 18px 0px;
      border-radius: 0px 0px 15px 15px;
    }
  }
  .ant-menu-item-divider {
    margin: 0px;
    border-width: 2px 0 0;
  }

  .no-data-found {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 55px;
    font-family: ${primaryFont};
    color: ${primaryColor};
    font-size: 18px;
    font-weight: ${wMedium};
    line-height: 34px;    
  }
`;

const searchResultsLoader = function searchResultsLoader() {
  return (
    <>
      {[1, 2, 3].map((item) => <AffinitySearchResultItemSkeleton key={item} />)}
    </>
  );
};

const SearchResultCount = styled.div`
  font-family: ${primaryFont};
  color: ${primaryColor};
  font-size: 18px;
  font-weight: ${wMedium};
  line-height: 34px;
  padding: 10px 16px;
  @media all and (max-width: ${maxWidth}) , ${retina} {
    padding: 9px 12px;
    font-size: 13px;
    line-height: 24px;
  }
`;

type Props = {
  // getScreenNameFun: () => void;
  onSelect: () => void;
};

export const AffinitySearchResults = function AffinitySearchResults(props: Props) {
  const dispatch = useDispatch();
  const { onSelect } = props;

  const loading: boolean = useSelector(selectLoading);
  const extendedLoading: boolean = useSelector(selectExtendedLoading);
  const loaded: boolean = useSelector(selectSearchLoaded);
  const extendedLoaded: boolean = useSelector(selectExtendedSearchLoaded);
  const peopleData: SearchResultSection = useSelector(selectPeopleData);
  // const errorText: string = useSelector(selectErrorText);
  const searchTerm: string = useSelector(selectSearchText);
  // const pagination: boolean = useSelector(selectIsPagination);
  const page: number = useSelector(selectSearchResultsPage);
  const selectedPersonId: number = useSelector(selectAffinitySelectedPersonId);

  const personProfileCard: PersonCard = useSelector(personCard);

  const { count, linkedInCount } = peopleData;
  const totalCount = linkedInCount ? count + linkedInCount : count;

  const persons: Executive[] = peopleData?.persons ?? [];

  const cData = useSelector(selectData);

  const onClickPerson = (person: Executive) => {
    const data: GetPersonProfileRequest = {
      personId: person.personId,
      type: person.personType,
      affinity_person: personProfileCard.personId,
    };
    dispatch(personProfileActions.getSearchedPersonProfileInsights(data));
    dispatch(actions.affinitySetSelectedPersonId(person.personId));
    onSelect();
    if (person.personType !== 'LIVESEARCH') {
      dispatch(personProfileActions.setSelectedSearchedPerson(person));
    }
    dispatch(certificationActions.certificationAction({
      type: 'affinity_person',
      source: 'person',
      data: cData,
    }));
  };

  const fetchMore = () => {
    if (!loading && !extendedLoading && loaded && extendedLoaded) {
      const obj: SearchResultsRequest = {
        query: searchTerm,
        screenName: 'person',
        page: page + 1,
        defaultLanguage: getLangCookie(),
      };
      dispatch(actions.affinitySearchResultsLoading(obj));
    }
  };

  return (
    searchTerm === '' && persons.length === 0 ? null
      : (
        <ItemsWrapper id="scrollableDiv">
          {page === 1 && persons.length === 0
            && (loading || extendedLoading) && searchResultsLoader()}
          {persons.length === 0 && !loading && !extendedLoading
            && loaded && extendedLoaded
            && (<div className="no-data-found">No person found</div>)}
          {page >= 1 && totalCount !== 0 && <SearchResultCount>{`Search Results (${totalCount})`}</SearchResultCount>}
          <InfiniteScroll
            hasMore={persons.length < totalCount}
            dataLength={persons?.length ?? 0}
            next={fetchMore}
            className="o-x-hidden o-y-hidden"
            loader={(loading || extendedLoading) && searchResultsLoader()}
            scrollableTarget="scrollableDiv"
          >
            {persons.map((person) => (
              <AffinitySearchResultItem
                key={person.personId}
                person={person}
                onClickHandler={() => onClickPerson(person)}
                isSelected={person.personId === selectedPersonId}
                isDisabled={person.personId === personProfileCard.personId}
              />
            ))}
          </InfiniteScroll>
        </ItemsWrapper>
      )
  );
};
